import type { ReactNode } from 'react';
import { SKELETON_WIDTHS, Skeleton, skeletonWidth } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A table's first load: placeholder rows under its real headers.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The console's half of `state-rules.md` §1 as the product owner amended it on 13 Oct 2026 — the
 * product's `DataList` draws the same `Skeleton` bars, so the two apps pulse alike. Goes inside a
 * `<tbody>`; the screen keeps one visually hidden `role="status"` line beside the table to speak
 * for the bars, which are `aria-hidden`.
 */
export function SkeletonRows({
  columns,
  rows = 6,
  rowClassName = 'h-10',
  rightAligned = [],
}: {
  readonly columns: number;
  readonly rows?: number;
  /** The real row's height class, so the table does not jump when the data lands. */
  readonly rowClassName?: string;
  /** Zero-based indexes of the columns whose figures sit at the right edge. */
  readonly rightAligned?: readonly number[];
}): ReactNode {
  return SKELETON_WIDTHS.slice(0, rows).map((_widths, row) => (
    <tr
      key={`skeleton-${String(row)}`}
      aria-hidden
      className={`${rowClassName} border-b last:border-0`}
    >
      {Array.from({ length: columns }, (_, column) => (
        <td key={column} className="px-3">
          <Skeleton
            width={skeletonWidth(row, column)}
            className={rightAligned.includes(column) ? 'ml-auto' : undefined}
          />
        </td>
      ))}
    </tr>
  ));
}
